import React from "react";

export type DrawerRoute = "style-yourself" | "saved-looks" | "items" | "history" | "insights";

type Props = {
  open: boolean;
  onClose: () => void;
  itemCount: number;
  historyCount: number;
  currentRoute: DrawerRoute;
  onNavigateRail: () => void;
  onNavigateSavedLooks: () => void;
  onNavigateItems: () => void;
  onNavigateHistory: () => void;
  onNavigateInsights: () => void;
  onAddItem: () => void;
  onOpenHelp: () => void;
};

function Row({
  icon,
  label,
  active = false,
  action = false,
  badge,
  showChevron = true,
  onClick,
}: {
  icon: string;
  label: string;
  active?: boolean;
  action?: boolean;
  badge?: number;
  showChevron?: boolean;
  onClick: () => void;
}) {
  const iconColor = active || action ? "#C84B2F" : "#2C2820";
  const labelColor = active || action ? "#C84B2F" : "#2C2820";
  const labelFont = active
    ? { fontFamily: "var(--font-display)", fontStyle: "italic" as const, fontWeight: 500 }
    : {};
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: 12,
        borderRadius: 12,
        marginBottom: 4,
        background: active ? "#FAECE7" : "transparent",
        border: "none",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      <i
        className={`ti ${icon}`}
        style={{ fontSize: 18, color: iconColor }}
        aria-hidden="true"
      />
      <span
        style={{
          flex: 1,
          fontSize: 14,
          color: labelColor,
          ...labelFont,
        }}
      >
        {label}
      </span>
      {typeof badge === "number" && (
        <span
          style={{
            background: "#F1EFE8",
            color: "#6B6560",
            borderRadius: 999,
            padding: "2px 7px",
            fontSize: 10,
            fontWeight: 500,
          }}
        >
          {badge}
        </span>
      )}
      {showChevron && !action && (
        <i
          className="ti ti-chevron-right"
          style={{ fontSize: 14, color: "#9B958E" }}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

export function WardrobeDrawer({
  open,
  onClose,
  itemCount,
  historyCount: _historyCount,
  currentRoute,
  onNavigateRail,
  onNavigateSavedLooks,
  onNavigateItems,
  onNavigateHistory,
  onNavigateInsights,
  onAddItem,
  onOpenHelp,
}: Props) {
  if (!open) return null;
  return (
    <>
      <style>{`
        @keyframes l2f-drawer-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(44,40,32,0.4)",
          zIndex: 90,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "78%",
          maxWidth: 340,
          background: "#FFFCF8",
          borderTopLeftRadius: 22,
          borderBottomLeftRadius: 22,
          paddingTop: "calc(var(--safe-top, 0px) + 14px)",
          paddingLeft: 16,
          paddingRight: 16,
          paddingBottom: "calc(20px + env(safe-area-inset-bottom))",
          display: "flex",
          flexDirection: "column",
          zIndex: 100,
          boxShadow: "-10px 0 24px rgba(44,40,32,0.18)",
          animation: "l2f-drawer-in 250ms ease-out",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <div>
            <p
              className="l2f-type-section-header"
              style={{
                color: "#C84B2F",
                margin: "0 0 4px",
              }}
            >
              YOUR WARDROBE
            </p>
            <p
              className="l2f-type-h2"
              style={{
                color: "#2C2820",
                margin: 0,
              }}
            >
              Menu
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: "#F1EFE8",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <i
              className="ti ti-x"
              style={{ fontSize: 14, color: "#6B6560" }}
              aria-hidden="true"
            />
          </button>
        </div>

        <Row
          icon="ti-sparkles"
          label="Style yourself"
          active={currentRoute === "style-yourself"}
          onClick={onNavigateRail}
        />
        <Row
          icon="ti-bookmark"
          label="Saved looks"
          active={currentRoute === "saved-looks"}
          onClick={onNavigateSavedLooks}
        />
        <Row
          icon="ti-hanger"
          label="Your items"
          badge={itemCount}
          active={currentRoute === "items"}
          onClick={onNavigateItems}
        />
        <Row
          icon="ti-history"
          label="History"
          active={currentRoute === "history"}
          onClick={onNavigateHistory}
        />
        <Row
          icon="ti-chart-bar"
          label="Insights"
          active={currentRoute === "insights"}
          onClick={onNavigateInsights}
        />
        <Row
          icon="ti-plus"
          label="Add items"
          action
          showChevron={false}
          onClick={onAddItem}
        />

        <div
          style={{
            borderTop: "0.5px solid #F1EFE8",
            margin: "14px 0",
          }}
        />

        <Row
          icon="ti-help"
          label="Help & support"
          showChevron={false}
          onClick={onOpenHelp}
        />
      </div>
    </>
  );
}
